import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import {
  LayoutContainer,
  HeaderWrapper,
  MainContent,
  FooterBlank,
  FixedContainer,
  ButtonContainer,
  DemoButton,
  ButtonText,
  ButtonLabel,
  ButtonHighlight,
  TopButton,
  TopButtonContainer,
  TopButtonImage,
  TopButtonWrapper,
} from "../styles/components/LayoutStyles";
import { ContentProvider } from '../context/ContentContext';

const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+";
const finalText = "START FOR DEMO";

function Layout({ children }) {
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const buttonLabelRef = useRef(null);
  const frameRef = useRef();
  const iterationRef = useRef(0);
  const isHoveringRef = useRef(false);
  const [isTopButtonVisible, setIsTopButtonVisible] = useState(false);

  const scrambleText = () => {
    if (!buttonLabelRef.current || !isHoveringRef.current) return;

    let result = "";
    let complete = true;
    const currentLength = Math.min(iterationRef.current + 1, finalText.length);

    // 현재 길이만큼 스크램블된 텍스트 생성
    for (let i = 0; i < currentLength; i++) {
      result += chars[Math.floor(Math.random() * chars.length)];
      complete = false;
    }

    // 나머지 공간은 빈칸으로 채움
    for (let i = currentLength; i < finalText.length; i++) {
      result += " ";
    }

    // 모든 글자가 스크램블되고 나서 한번 더 반복하면 최종 텍스트로 변경
    if (iterationRef.current > finalText.length) {
      result = finalText;
      complete = true;
    }

    buttonLabelRef.current.textContent = result;

    if (!complete && isHoveringRef.current) {
      iterationRef.current++;
      frameRef.current = requestAnimationFrame(scrambleText);
    } else {
      if (isHoveringRef.current) {
        buttonLabelRef.current.textContent = finalText;
      }
      isHoveringRef.current = false;
      iterationRef.current = 0;
    }
  };

  const handleMouseEnter = () => {
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }
    isHoveringRef.current = true;
    iterationRef.current = 0;
    scrambleText();
  };

  const handleMouseLeave = () => {
    isHoveringRef.current = false;
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
    }
    if (buttonLabelRef.current) {
      buttonLabelRef.current.textContent = finalText;
    }
    iterationRef.current = 0;
  };

  const handleTopButtonClick = (e) => {
    e.preventDefault();
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsTopButtonVisible(scrollY > 200);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (buttonLabelRef.current) {
      buttonLabelRef.current.textContent = finalText;
    }
  }, []);

  return (
    <ContentProvider>
      <LayoutContainer>
        <HeaderWrapper>
          <Header />
        </HeaderWrapper>
        <MainContent>{children}</MainContent>
        <FooterBlank />
        <Footer />
        <FixedContainer>
          <ButtonContainer>
            <DemoButton
              href="https://www.figma.com/proto/QWapuwRTzHroMbMsWcwWUB/Titan?node-id=3012-107&t=rI9yab1PGH7OjoDE-1"
              target="_blank"
              rel="noopener"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <ButtonText>
                <ButtonLabel ref={buttonLabelRef} />
              </ButtonText>
              <ButtonHighlight />
            </DemoButton>
          </ButtonContainer>
        </FixedContainer>
        <TopButton href="#main-1" onClick={handleTopButtonClick}>
          <TopButtonContainer className={isTopButtonVisible ? 'visible' : ''}>
            <TopButtonWrapper>
              <TopButtonImage
                decoding="async"
                width="1037"
                height="778"
                sizes="(min-width: 1440px) 60px, (min-width: 810px) and (max-width: 1439px) 60px, (max-width: 809px) 40px"
                srcSet="https://framerusercontent.com/images/E8kFwoAH6xoJoBNWASPp4It2fw.png?scale-down-to=512 512w,https://framerusercontent.com/images/E8kFwoAH6xoJoBNWASPp4It2fw.png?scale-down-to=1024 1024w,https://framerusercontent.com/images/E8kFwoAH6xoJoBNWASPp4It2fw.png 1037w"
                src="https://framerusercontent.com/images/E8kFwoAH6xoJoBNWASPp4It2fw.png"
                alt="Top button"
              />
            </TopButtonWrapper>
          </TopButtonContainer>
        </TopButton>
      </LayoutContainer>
    </ContentProvider>
  );
}

export default Layout;
